import { useCallback, useEffect, useMemo, useState } from "react";
import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

import { db } from "../../../backend/firebaseConfig";
import { normaliseParaplanningEmail } from "../utils/paraplanningBonusUtils";

const SALARIES_COLLECTION_PATH = [
  "Paraplanning",
  "Settings",
  "ParaplannerSalaries",
];

const getSalaryDocId = (email = "") => {
  return normaliseParaplanningEmail(email).replaceAll("/", "_");
};

export const useParaplannerSalaries = (enabled = true) => {
  const [salaries, setSalaries] = useState([]);
  const [loadingSalaries, setLoadingSalaries] = useState(false);
  const [salariesError, setSalariesError] = useState("");

  const refreshSalaries = useCallback(async () => {
    if (!enabled) return;

    setLoadingSalaries(true);
    setSalariesError("");

    try {
      const salaryRef = collection(db, ...SALARIES_COLLECTION_PATH);
      const snapshot = await getDocs(salaryRef);

      const records = snapshot.docs.map((salaryDoc) => ({
        id: salaryDoc.id,
        ...salaryDoc.data(),
      }));

      setSalaries(records);
    } catch (error) {
    //   console.error("[Paraplanning Salaries] Failed to fetch salaries:", error);
      setSalariesError("Unable to load paraplanner salaries.");
    } finally {
      setLoadingSalaries(false);
    }
  }, [enabled]);

 const saveSalary = useCallback(
   async ({
     email,
     name,
     monthlySalary,
     annualSalary,
     effectiveFromMonth,
     updatedBy,
   }) => {
     const safeEmail = normaliseParaplanningEmail(email);

     const monthly = Number(monthlySalary || 0);
     const annual = Number(annualSalary || monthly * 12 || 0);

     if (!safeEmail) {
       throw new Error("Paraplanner email is required.");
     }

     if (!monthly || monthly <= 0) {
       throw new Error("Monthly salary must be greater than 0.");
     }

     if (!annual || annual <= 0) {
       throw new Error("Annual salary must be greater than 0.");
     }

     if (!effectiveFromMonth) {
       throw new Error("Effective from month is required.");
     }

     const salaryHistoryItem = {
       id: `${effectiveFromMonth}-${Date.now()}`,
       monthlySalary: monthly,
       annualSalary: annual,
       effectiveFromMonth,
       createdAtMs: Date.now(),
       updatedBy: updatedBy || "",
     };

     const salaryDocRef = doc(
       db,
       ...SALARIES_COLLECTION_PATH,
       getSalaryDocId(safeEmail),
     );

    //  console.log("[Paraplanner Salary Save]", {
    //    email: safeEmail,
    //    name,
    //    monthlySalary: monthly,
    //    annualSalary: annual,
    //    calculation: `${monthly} × 12 = ${annual}`,
    //    effectiveFromMonth,
    //    updatedBy,
    //  });

     await setDoc(
       salaryDocRef,
       {
         email: safeEmail,
         name: name || "",
         monthlySalary: monthly,
         annualSalary: annual,
         effectiveFromMonth,
         updatedAt: serverTimestamp(),
         updatedAtMs: Date.now(),
         updatedBy: updatedBy || "",
         salaryHistory: arrayUnion(salaryHistoryItem),
       },
       { merge: true },
     );

     await refreshSalaries();
   },
   [refreshSalaries],
 );

  useEffect(() => {
    refreshSalaries();
  }, [refreshSalaries]);

  const salaryMap = useMemo(() => {
    return salaries.reduce((acc, salary) => {
      const email = normaliseParaplanningEmail(salary.email);

      if (email) {
        acc[email] = salary;
      }

      return acc;
    }, {});
  }, [salaries]);

  return {
    salaries,
    salaryMap,
    loadingSalaries,
    salariesError,
    refreshSalaries,
    saveSalary,
  };
};
